import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { DbUserApp, getDbUserApps, updateDbUser } from "@/services/dbUsers";

const editableSummaryFields = [
  "email",
  "ownerEmail",
  "plan",
  "wixDomain",
  "installed",
  "disabled",
];

const formatValue = (value: unknown) => {
  if (value == null) return "";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const getEditableUser = (user: Record<string, unknown>) => {
  const { id: _id, ...rest } = user;
  return rest;
};

const AdminAllDbsPage: React.FC = () => {
  const { accessKey } = useParams();
  const expectedKey = import.meta.env.VITE_ADMIN_ROUTE_KEY as string | undefined;
  const routeAllowed = Boolean(expectedKey && accessKey === expectedKey);

  const [adminKey, setAdminKey] = useState(
    () => localStorage.getItem("portfolio-admin-key") || "",
  );
  const [apps, setApps] = useState<DbUserApp[]>([]);
  const [selectedUser, setSelectedUser] = useState<{
    app: DbUserApp;
    user: Record<string, unknown> & { id: string };
  } | null>(null);
  const [editorText, setEditorText] = useState("");
  const [sensitiveField, setSensitiveField] = useState("password");
  const [sensitiveValue, setSensitiveValue] = useState("");
  const [lastPasswordValue, setLastPasswordValue] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const totalUsers = useMemo(
    () => apps.reduce((total, app) => total + app.count, 0),
    [apps],
  );

  useEffect(() => {
    if (!selectedUser) {
      setEditorText("");
      return;
    }

    setEditorText(JSON.stringify(getEditableUser(selectedUser.user), null, 2));
  }, [selectedUser]);

  if (!routeAllowed) {
    return <Navigate to="/" replace />;
  }

  const loadApps = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      localStorage.setItem("portfolio-admin-key", adminKey);
      setApps(await getDbUserApps(adminKey));
      setMessage("Database users loaded.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Load failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveJson = async () => {
    if (!selectedUser) return;

    setMessage("");
    try {
      const updates = JSON.parse(editorText) as Record<string, unknown>;
      await updateDbUser(
        {
          dbName: selectedUser.app.dbName,
          collectionName: selectedUser.app.collectionName,
          userId: selectedUser.user.id,
          updates,
        },
        adminKey,
      );
      await loadApps();
      setMessage("User updated.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Update failed.");
    }
  };

  const handleQuickUpdate = async (field: string, value: string) => {
    if (!selectedUser) return;

    setMessage("");
    await updateDbUser(
      {
        dbName: selectedUser.app.dbName,
        collectionName: selectedUser.app.collectionName,
        userId: selectedUser.user.id,
        updates: { [field]: value },
      },
      adminKey,
    );
    await loadApps();
    setMessage(`${field} updated.`);
  };

  const handleSensitiveUpdate = async () => {
    if (!selectedUser) return;

    setMessage("");
    await updateDbUser(
      {
        dbName: selectedUser.app.dbName,
        collectionName: selectedUser.app.collectionName,
        userId: selectedUser.user.id,
        sensitiveUpdate: {
          field: sensitiveField,
          value: sensitiveValue,
        },
      },
      adminKey,
    );
    setLastPasswordValue(sensitiveField.toLowerCase().includes("password") ? sensitiveValue : "");
    setSensitiveValue("");
    await loadApps();
    setMessage(`${sensitiveField} set. It will remain hidden in the UI.`);
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#f6f4ef] px-4 py-8 text-[#181818] sm:px-5 md:px-10 lg:px-16">
      <section className="mx-auto w-full max-w-7xl">
        <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-black/45">
              Private Dashboard
            </p>
            <h1
              className="mt-3 uppercase leading-none"
              style={{
                fontFamily: "'League Gothic', sans-serif",
                fontSize: "clamp(3.4rem, 9vw, 8rem)",
              }}
            >
              All DBs
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={`/manage/${accessKey}`}
              className="rounded-full border border-black/20 px-5 py-3 text-xs uppercase tracking-[0.18em] transition hover:bg-white"
            >
              Projects
            </a>
            <a
              href="/"
              className="rounded-full border border-black/20 px-5 py-3 text-xs uppercase tracking-[0.18em] transition hover:bg-black hover:text-white"
            >
              Portfolio
            </a>
          </div>
        </div>

        <div className="mb-8 grid gap-4 border-y border-black/15 py-6 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-end">
          <label className="grid gap-2 text-sm font-medium">
            Admin API Key
            <input
              value={adminKey}
              onChange={(event) => setAdminKey(event.target.value)}
              className="min-w-0 border border-black/15 bg-white px-4 py-3 outline-none focus:border-black"
              type="password"
              required
            />
          </label>
          <button
            onClick={loadApps}
            disabled={isLoading || !adminKey}
            className="w-full rounded-full bg-black px-6 py-3 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-black/80 disabled:opacity-50 sm:w-auto"
          >
            {isLoading ? "Loading" : "Load DBs"}
          </button>
          <div className="text-sm text-black/55">
            {apps.length} app DBs · {totalUsers} users
          </div>
        </div>

        {message && <p className="mb-6 text-sm text-black/60">{message}</p>}

        <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
          <section className="grid min-w-0 gap-5">
            {apps.map((app) => (
              <article key={`${app.dbName}.${app.collectionName}`} className="min-w-0 border border-black/10 bg-white p-4 sm:p-5">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-black/40">
                      {app.collectionName}
                    </p>
                    <h2 className="mt-1 text-2xl font-semibold">{app.dbName}</h2>
                  </div>
                  <span className="rounded-full border border-black/15 px-3 py-1 text-xs uppercase tracking-[0.16em] text-black/55">
                    {app.count} users
                  </span>
                </div>

                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[720px] border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-black/10 text-xs uppercase tracking-[0.15em] text-black/40">
                        <th className="py-3 pr-4">User</th>
                        <th className="py-3 pr-4">Plan</th>
                        <th className="py-3 pr-4">Domain</th>
                        <th className="py-3 pr-4">Status</th>
                        <th className="py-3 pr-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {app.users.map((user) => (
                        <tr key={user.id} className="border-b border-black/5">
                          <td className="py-3 pr-4">
                            <p className="font-medium">
                              {formatValue(user.email || user.name || user.username || user.id)}
                            </p>
                            <p className="text-xs text-black/45">{user.id}</p>
                          </td>
                          <td className="py-3 pr-4">{formatValue(user.plan)}</td>
                          <td className="max-w-[220px] truncate py-3 pr-4">
                            {formatValue(user.wixDomain || user.domain || user.siteDomain)}
                          </td>
                          <td className="py-3 pr-4">{formatValue(user.status || user.role)}</td>
                          <td className="py-3 pr-4">
                            <button
                              onClick={() => setSelectedUser({ app, user })}
                              className="rounded-full border border-black/20 px-4 py-2 text-xs uppercase tracking-[0.15em] transition hover:bg-black hover:text-white"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="grid gap-3 md:hidden">
                  {app.users.map((user) => (
                    <div key={user.id} className="border border-black/10 bg-[#f6f4ef] p-4">
                      <p className="break-words font-semibold">
                        {formatValue(user.email || user.name || user.username || user.id)}
                      </p>
                      <p className="mt-1 break-all text-xs text-black/45">{user.id}</p>
                      <div className="mt-4 grid gap-2 text-sm text-black/65">
                        <p><span className="font-semibold text-black">Plan:</span> {formatValue(user.plan)}</p>
                        <p className="break-words"><span className="font-semibold text-black">Domain:</span> {formatValue(user.wixDomain || user.domain || user.siteDomain)}</p>
                        <p><span className="font-semibold text-black">Status:</span> {formatValue(user.status || user.role || (user.installed ? "installed" : ""))}</p>
                      </div>
                      <button
                        onClick={() => setSelectedUser({ app, user })}
                        className="mt-4 rounded-full border border-black/20 px-4 py-2 text-xs uppercase tracking-[0.15em] transition hover:bg-black hover:text-white"
                      >
                        Edit
                      </button>
                    </div>
                  ))}
                </div>
              </article>
            ))}

            {!apps.length && (
              <div className="border border-black/10 bg-white p-8 text-sm leading-7 text-black/60">
                Add your admin API key and load databases. It scans the MongoDB
                cluster from `MONGO_URI` and shows each app database that has a
                `users` or `user` collection.
              </div>
            )}
          </section>

          <aside className="min-w-0 border border-black/10 bg-white p-4 sm:p-5 xl:sticky xl:top-6 xl:h-fit">
            <h2 className="text-xl font-semibold">Edit User</h2>
            {selectedUser ? (
              <div className="mt-5 grid gap-5">
                <div className="text-sm text-black/60">
                  <p>
                    <span className="font-semibold text-black">DB:</span>{" "}
                    {selectedUser.app.dbName}
                  </p>
                  <p>
                    <span className="font-semibold text-black">User:</span>{" "}
                    {selectedUser.user.id}
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {editableSummaryFields.map((field) => (
                    <label key={field} className="grid gap-1 text-xs font-medium uppercase tracking-[0.12em] text-black/45">
                      {field}
                      <input
                        defaultValue={formatValue(selectedUser.user[field])}
                        onBlur={(event) => {
                          if (event.target.value !== formatValue(selectedUser.user[field])) {
                            handleQuickUpdate(field, event.target.value);
                          }
                        }}
                        className="min-w-0 border border-black/15 bg-[#f6f4ef] px-3 py-2 text-sm normal-case tracking-normal text-black outline-none focus:border-black"
                      />
                    </label>
                  ))}
                </div>

                <label className="grid gap-2 text-sm font-medium">
                  Full editable JSON
                  <textarea
                    value={editorText}
                    onChange={(event) => setEditorText(event.target.value)}
                    className="min-h-[260px] min-w-0 border border-black/15 bg-[#f6f4ef] px-4 py-3 font-mono text-xs leading-5 outline-none focus:border-black"
                  />
                </label>
                <button
                  onClick={handleSaveJson}
                  className="rounded-full bg-black px-5 py-3 text-xs uppercase tracking-[0.18em] text-white transition hover:bg-black/80"
                >
                  Save JSON
                </button>

                <div className="border-t border-black/10 pt-5">
                  <p className="mb-3 text-xs uppercase tracking-[0.18em] text-black/45">
                    Reset password / hidden field
                  </p>
                  <div className="grid gap-3 sm:grid-cols-[0.8fr_1.2fr]">
                    <select
                      value={sensitiveField}
                      onChange={(event) => setSensitiveField(event.target.value)}
                      className="border border-black/15 bg-[#f6f4ef] px-3 py-2 text-sm outline-none focus:border-black"
                    >
                      <option value="password">password</option>
                    </select>
                    <input
                      value={sensitiveValue}
                      onChange={(event) => setSensitiveValue(event.target.value)}
                      className="border border-black/15 bg-[#f6f4ef] px-3 py-2 text-sm outline-none focus:border-black"
                      type="password"
                      placeholder="new value"
                    />
                  </div>
                  <button
                    onClick={handleSensitiveUpdate}
                    disabled={!sensitiveField || !sensitiveValue}
                    className="mt-3 rounded-full border border-black/20 px-5 py-3 text-xs uppercase tracking-[0.18em] transition hover:bg-black hover:text-white disabled:opacity-40"
                  >
                    {sensitiveField.toLowerCase().includes("password")
                      ? "Hash & Save Password"
                      : "Set Field"}
                  </button>
                  <p className="mt-3 text-xs leading-5 text-black/45">
                    Password fields are saved as bcrypt hashes in MongoDB and
                    are never displayed as hashes in this panel.
                  </p>
                  {lastPasswordValue && (
                    <p className="mt-3 break-all border border-black/10 bg-[#f6f4ef] p-3 text-xs leading-5 text-black/60">
                      Last password you set in this browser session:{" "}
                      <span className="font-semibold text-black">
                        {lastPasswordValue}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm leading-7 text-black/55">
                Select any user from the detected database sections to edit plan,
                domain, status, or the full record.
              </p>
            )}
          </aside>
        </div>
      </section>
    </main>
  );
};

export default AdminAllDbsPage;
